import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// V2: `agency_id` (= selectedBrand.uid = agency_id_no_r) replaces `travel_agency_uid`, and the
// recipient is addressed explicitly via `recipient_user_uid`.
//
// The read and write endpoints handle agency scope differently on the backend, so the two helpers
// below are deliberately separate rather than one shared builder:
//   - reads  → `agency_id` optional; when omitted the BE resolves it from `user_uid`.
//   - writes → `agency_id` mandatory; omitting it returns ERROR_MISSING_REQUIRED_PARAMETER.
// Callers pass `agencyId` from `selectRoyalMomentumSelectedBrand`. See V2_API_MIGRATION.md.
const readScope = (agencyId) => {
    const userDetails = getUserDetails();
    const uid = userDetails?.user_details?.uid;
    return {
        user_uid: uid,
        recipient_user_uid: uid,
        // Omitted rather than sent as null/0 — the BE treats a falsy agency_id as "resolve from
        // user_uid", and an explicit null would break that.
        ...(agencyId ? { agency_id: agencyId } : {}),
    };
};

const writeScope = (agencyId) => {
    const userDetails = getUserDetails();
    const uid = userDetails?.user_details?.uid;
    return {
        user_uid: uid,
        recipient_user_uid: uid,
        agency_id: agencyId,
    };
};

export const royalMomentumNotificationsApi = createApi({
    reducerPath: "royalMomentumNotificationsApi",
    baseQuery: apiRequestBaseQuery,
    tagTypes: ["Notifications", "NotificationUnreadCount"],
    endpoints: (builder) => ({
        getNotifications: builder.query({
            query: (params = {}) => ({
                requestType: requestType.getNotificationsV2,
                data: {
                    ...readScope(params.agencyId),
                    page: params.page || 1,
                    limit: params.limit || 20,
                    // Optional exact-match filters supported by V2.
                    ...(params.category ? { category: params.category } : {}),
                    ...(params.type ? { type: params.type } : {}),
                    ...(params.isRead === 0 || params.isRead === 1
                        ? { is_read: params.isRead }
                        : {}),
                },
            }),
            providesTags: ["Notifications"],
        }),

        getNotificationUnreadCount: builder.query({
            query: (params = {}) => ({
                requestType: requestType.getNotificationUnreadCountV2,
                data: readScope(params?.agencyId),
            }),
            providesTags: ["NotificationUnreadCount"],
        }),

        getNotificationDetail: builder.query({
            query: (params = {}) => ({
                requestType: requestType.getNotificationDetailV2,
                data: {
                    ...readScope(params.agencyId),
                    notification_uid: params.notificationUid,
                },
            }),
            providesTags: ["Notifications"],
        }),

        markAllNotificationsRead: builder.mutation({
            // `{ agencyId, category }` — category scopes the bulk update to one tab.
            query: (params = {}) => ({
                requestType: requestType.markAllNotificationsReadV2,
                data: {
                    ...writeScope(params.agencyId),
                    ...(params.category ? { category: params.category } : {}),
                },
            }),
            invalidatesTags: ["Notifications", "NotificationUnreadCount"],
        }),

        markNotificationRead: builder.mutation({
            // `{ notificationUid, agencyId }` — agencyId is required by the V2 endpoint.
            query: (params = {}) => ({
                requestType: requestType.markNotificationReadV2,
                data: {
                    ...writeScope(params.agencyId),
                    notification_uid: params.notificationUid,
                },
            }),
            invalidatesTags: ["Notifications", "NotificationUnreadCount"],
        }),

        deleteNotification: builder.mutation({
            // Soft delete (sets deleted_at). No UI wired to this yet.
            query: (params = {}) => ({
                requestType: requestType.deleteNotificationV2,
                data: {
                    ...writeScope(params.agencyId),
                    notification_uid: params.notificationUid,
                },
            }),
            invalidatesTags: ["Notifications", "NotificationUnreadCount"],
        }),
    }),
});

export const {
    useGetNotificationsQuery,
    useGetNotificationUnreadCountQuery,
    useGetNotificationDetailQuery,
    useMarkAllNotificationsReadMutation,
    useMarkNotificationReadMutation,
    useDeleteNotificationMutation,
} = royalMomentumNotificationsApi;
